<script setup lang="ts">
import HorizontalGeneralCard from '@/components/cards/HorizontalGeneralCard.vue'
import HorizontalDownloadCard from '@/components/cards/HorizontalDownloadCard.vue'
import { getStringOrFirstElement } from '@/utils/array'
import type { Article, Video } from '@/models/content'
import { POST_TYPES } from '@/constants/content'
import { toTitleCase } from '@/utils/string'
import { useGoogleAnalyticsPageView } from '~/composables/google-analytics'
import { useAsyncData } from '#app'
import { useAuthStore } from '~/stores/auth'
import BriefingsSection from '~/components/briefings/BriefingsSection.vue'
import type { TCategoryBriefings } from '~/types/briefings'
import { ResponseStatus } from '~/types/app.type'

const latestSectionCards = {
  article: HorizontalGeneralCard,
  video: HorizontalGeneralCard,
  presentation: HorizontalDownloadCard,
  magazine: HorizontalDownloadCard,
}

const arrayPills = (data: Video | Article) => {
  return useFormattedPillsData(data)
}

const { $graphqlClient, $restClient, $seoManager } = useNuxtApp()
const route = useRoute()
const authStore = useAuthStore()
const { getBriefings } = useBriefings()

const category = getStringOrFirstElement(route.params.category)
const subcategory = getStringOrFirstElement(route.params.subcategory)
const categories = subcategory ? [category, subcategory] : [category]

const urlSearch = `/search/category/${category}`

const isAuthenticated = computed(() => authStore.isAuthenticated)

const seo = $seoManager.generateSeoInfo({
  title: toTitleCase(category),
})
useHead(seo)
useGoogleAnalyticsPageView(seo.title)

const fetchFeaturedArticles = () => {
  return $graphqlClient.getFeaturedArticlesByCategories({
    itemsPerQuery: 4,
    categories,
  })
}

const fetchVideosByCategories = async () => {
  /*-------------------------------------
    SECTION: VIDEO
  -------------------------------------*/
  const { data } = await $graphqlClient.getVideosByCategories({
    itemsPerQuery: 3,
    categories,
  })
  return data
}

const fetchMoreFromBenchmark = async (excludedIds) => {
  /*-------------------------------------
    SECTION: MORE FROM BENCHMARK
  -------------------------------------*/
  const MAX_LATEST_CONTENT_RESULTS = 12

  const { data } = await $restClient.getSearchResult({
    post_types: POST_TYPES,
    categories,
    items_per_page: MAX_LATEST_CONTENT_RESULTS,
    excluded_ids: excludedIds,
  })

  return data
}

const { data: featuredArticles, status: featuredArticlesStaus } = useAsyncData(
  `featured-articles-${category}-page`,
  async () => {
    const featuredArticles = await fetchFeaturedArticles()

    return {
      featuredArticlesIds: featuredArticles.map((article) => article.id),
      firstFeaturedArticle: featuredArticles[0],
      remainingFeaturedArticle: featuredArticles.slice(1),
    }
  },
  {
    server: false,
    watch: [isAuthenticated],
    default: () => ({
      featuredArticlesIds: [],
      firstFeaturedArticle: null,
      remainingFeaturedArticle: [],
    }),
  },
)

const { data: videosData, status: videosStatus } = useAsyncData(
  `videos-${category}-page`,
  async () => {
    const videos = await fetchVideosByCategories()
    return {
      videosIds: videos.map((video) => video.id),
      firstVideo: videos[0],
      videos,
    }
  },
  {
    server: false,
    watch: [isAuthenticated],
    default: () => ({
      videosIds: [],
      firstVideo: null,
      videos: [],
    }),
  },
)

const { data: briefings, status: briefingsStatus } = useAsyncData(
  `briefings-${category}-page`,
  async () => {
    return getBriefings([category as TCategoryBriefings], 8)
  },
  {
    server: false,
    watch: [isAuthenticated],
    default: () => [],
  },
)

const firstFeaturedArticle = computed(
  () => featuredArticles.value.firstFeaturedArticle,
)
const remainingFeaturedArticle = computed(
  () => featuredArticles.value.remainingFeaturedArticle,
)
const firstVideo = computed(() => videosData.value.firstVideo)
const videos = computed(() => videosData.value.videos)

const { data: contents, status: contentsStatus } = useAsyncData(
  `${category}-page`,
  async () => {
    const excludedIds = [
      ...featuredArticles.value.featuredArticlesIds,
      ...videosData.value.videosIds,
    ]
    return fetchMoreFromBenchmark(excludedIds)
  },
  {
    server: false,
    watch: [isAuthenticated, featuredArticles, videosData],
    default: () => ({
      contents: [],
    }),
  },
)

const loadingFeaturedArticles = computed(
  () => featuredArticlesStaus.value !== ResponseStatus.SUCCESS,
)
const loadingVideos = computed(
  () =>
    !loadingFeaturedArticles.value &&
    videosStatus.value !== ResponseStatus.SUCCESS,
)
const loadingBriefings = computed(
  () =>
    !loadingFeaturedArticles.value &&
    !loadingVideos.value &&
    briefingsStatus.value !== ResponseStatus.SUCCESS,
)
const loadingContents = computed(
  () =>
    !loadingFeaturedArticles.value &&
    !loadingVideos.value &&
    !loadingBriefings.value &&
    contentsStatus.value !== ResponseStatus.SUCCESS,
)
</script>

<template>
  <div class="mb-14 flex grow items-center justify-center md:mb-25">
    <div class="w-full pb-0 lg:mt-10">
      <LoadingSpinner v-if="loadingFeaturedArticles" wrap-screen />
      <template v-else>
        <div
          v-if="firstFeaturedArticle"
          class="mx-auto max-w-content-container lg:px-5"
        >
          <FeaturedCategoryCard
            data-testid="featured-category-card"
            :published-date="firstFeaturedArticle.publishedDate"
            :content-type="firstFeaturedArticle.contentType"
            :slug="firstFeaturedArticle.slug"
            :duration="firstFeaturedArticle.duration"
            :title="firstFeaturedArticle.title"
            :excerpt="firstFeaturedArticle.excerpt"
            :pills="arrayPills(firstFeaturedArticle)"
            :cover-url="firstFeaturedArticle.coverUrl"
          />
        </div>
        <div
          v-if="remainingFeaturedArticle"
          class="mx-auto mt-6 max-w-content-container px-5 md:mt-8"
        >
          <!-- Desktop -->
          <div class="hidden grid-cols-3 gap-5 lg:grid">
            <VerticalCard
              v-for="article in remainingFeaturedArticle"
              :key="article.id"
              content-type="article"
              :published-date="article.publishedDate"
              :slug="article.slug"
              :duration="article.duration"
              :title="article.title"
              :excerpt="article.excerpt"
              :cover-url="article.coverUrl"
              :pills="arrayPills(article)"
            />
          </div>

          <!-- Mobile -->
          <div class="lg:hidden">
            <HorizontalGeneralCard
              v-for="article in remainingFeaturedArticle"
              :key="article.id"
              class="show-first-three md:show-all md:hide-2nd-card-border border-t-2 border-s-200 first:border-none"
              :published-date="article.publishedDate"
              :slug="article.slug"
              :duration="article.duration"
              :title="article.title"
              :excerpt="article.excerpt"
              :categories="article.tags"
              :cover-url="article.coverUrl"
              :pills="article.tags"
            />
          </div>
        </div>
      </template>

      <!-- Part Two -->
      <LoadingSpinner v-if="loadingVideos" />
      <VideosSection
        v-else-if="videos?.length"
        :featured-video="firstVideo"
        :videos="videos"
      />

      <LoadingSpinner v-if="loadingBriefings" />
      <BriefingsSection :briefings="briefings" />

      <!-- Part Three -->
      <LoadingSpinner v-if="loadingContents" />
      <BaseSection
        v-else-if="contents?.length"
        title="More from Benchmark"
        data-testid="latest-section"
      >
        <div class="grid-cols-2 flex-wrap gap-x-5 md:grid">
          <component
            :is="latestSectionCards[content.contentType]"
            v-for="content in contents"
            :key="content.id"
            v-bind="content"
            :pills="content.tags"
            class="border-t-2 border-s-200 first:border-none md:[&:nth-child(2)]:border-none"
            data-testid="latest-card"
          />
        </div>
      </BaseSection>
      <div class="mt-2 flex justify-center md:mt-7">
        <BaseLink
          variant="tertiary"
          :href="urlSearch"
          data-testid="category-archive-link"
        >
          <span class="md:hidden">See more</span>
          <span class="hidden md:block">Click to view more from Benchmark</span>
        </BaseLink>
      </div>
    </div>
  </div>
</template>
